<template>
    <div class="product-batch-modal">
        <template v-if="!searched">
            <p class="hint">粘贴产品名称、包名或 ID，每行一个。查询后可逐项、全选或按媒体选择匹配结果。</p>
            <el-input v-model="input" type="textarea" :rows="8" resize="none" placeholder="每行一个产品名称、包名或 ID" />
        </template>
        <template v-else>
            <div class="summary">
                <span>匹配 {{products.length}} 项，已选 {{checkedKeys.length}} 项</span>
                <el-button type="text" @click="searched = false">修改输入</el-button>
            </div>
            <div v-if="unmatched.length" class="unmatched">
                未匹配 {{unmatched.length}} 条，加入时将忽略：{{unmatched.join('、')}}
            </div>
            <template v-if="products.length">
                <div class="actions">
                    <el-button type="text" @click="selectAll">全选匹配</el-button>
                    <el-button type="text" @click="clearAll">取消全选</el-button>
                    <el-select v-model="media" size="small" clearable placeholder="按媒体选择" class="media-filter"
                               @change="selectMedia">
                        <el-option label="全部媒体（全选）" :value="0" />
                        <el-option v-for="item in resultMedia" :key="item.id" :label="item.name" :value="item.id" />
                    </el-select>
                </div>
                <el-checkbox-group v-model="checkedKeys" class="matches" @change="media = ''">
                    <el-checkbox v-for="product in products" :key="identity(product)" :label="identity(product)"
                                 class="match">
                        <span class="match-text" :title="label(product)">
                            <span class="match-name">{{product.product_name || '-'}}</span>
                            <span class="match-meta">{{meta(product)}}<template v-if="product.subproject_content"> · 包名 {{product.subproject_content}}</template></span>
                        </span>
                    </el-checkbox>
                </el-checkbox-group>
            </template>
            <p v-else class="empty">没有匹配的产品，请修改输入后重试。</p>
        </template>
        <div class="footer">
            <el-button @click="$emit('close')">取消</el-button>
            <el-button v-if="!searched" type="primary" :loading="loading" @click="search">查询匹配</el-button>
            <el-button v-else type="primary" :disabled="!checkedKeys.length" @click="apply">
                加入已选 {{checkedKeys.length}} 项
            </el-button>
        </div>
    </div>
</template>

<script>
export default {
    props: { cbs: { type: Object, required: true } },
    data() {
        return { input: '', loading: false, searched: false, products: [], unmatched: [], checkedKeys: [], media: 0 };
    },
    computed: {
        resultMedia() {
            const media = new Map();
            this.products.forEach(product => media.set(product.product_plat_id,
                product.product_plat_name || String(product.product_plat_id)));
            return [...media].map(([id, name]) => ({ id, name }));
        },
    },
    methods: {
        identity(product) { return `${product.product_source}:${product.product_plat_id}:${product.product_key}`; },
        label(product) {
            return `${product.product_name || '-'}｜${this.meta(product)}${product.subproject_content ? `｜包名 ${product.subproject_content}` : ''}`;
        },
        meta(product) { return `${product.product_plat_name || '-'}｜ID ${product.display_id || product.product_key}`; },
        async search() {
            const terms = [...new Set(this.input.split(/\r?\n/).map(item => item.trim()).filter(Boolean))];
            if (!terms.length) { this.$message.warning('请先输入产品名称、包名或 ID'); return; }
            if (terms.length > 200) { this.$message.warning('每次最多粘贴 200 项'); return; }
            this.loading = true;
            try {
                const result = await this.cbs.onSearch(terms);
                if (result.truncated) { this.$message.warning('匹配结果超过 200 项，请分批粘贴'); return; }
                this.products = result.products || [];
                this.unmatched = result.err_list || [];
                this.checkedKeys = this.products.map(this.identity);
                this.media = 0;
                this.searched = true;
            } catch (error) {
                this.$message.error(error.info || '产品查询失败');
            } finally {
                this.loading = false;
            }
        },
        selectAll() {
            this.checkedKeys = this.products.map(this.identity);
            this.media = 0;
        },
        clearAll() {
            this.checkedKeys = [];
            this.media = '';
        },
        selectMedia(media) {
            this.checkedKeys = media === '' || media === 0 ? this.products.map(this.identity) :
                this.products.filter(product => String(product.product_plat_id) === String(media)).map(this.identity);
        },
        apply() {
            const selected = new Set(this.checkedKeys);
            this.cbs.onApply(this.products.filter(product => selected.has(this.identity(product))));
            this.$emit('close');
        },
    },
};
</script>

<style lang="scss" scoped>
.product-batch-modal {
    .hint { margin: 0 0 12px; color: #606266; }
    .summary, .actions { display: flex; align-items: center; gap: 12px; }
    .summary { justify-content: space-between; margin-bottom: 8px; }
    .unmatched { max-height: 60px; overflow-y: auto; margin-bottom: 10px; color: #e6a23c; word-break: break-all; }
    .actions { margin-bottom: 10px; }
    .media-filter { width: 150px; margin-left: auto; }
    .matches { max-height: 260px; overflow-y: auto; border: 1px solid #ebeef5; border-radius: 4px; }
    .match { display: flex; align-items: center; margin: 0; padding: 7px 10px; border-bottom: 1px solid #f2f3f5; }
    .match:last-child { border-bottom: 0; }
    .match ::v-deep .el-checkbox__label { flex: 1; min-width: 0; }
    .match-text { display: flex; flex-direction: column; min-width: 0; line-height: 18px; }
    .match-name, .match-meta { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .match-meta { color: #909399; font-size: 12px; }
    .empty { color: #909399; text-align: center; }
    .footer { display: flex; justify-content: flex-end; gap: 10px; margin-top: 18px; }
}
</style>
